import moment from 'moment';

export const DATE_FORMAT = 'YYYY/MM/DD';

/**
 * 每個日期底下的子欄位。可售擺第一個 —— 它是業務最常看的數字。
 *
 * 房數 (room_qnt) 不列在這裡：同一房型的房數在各日期都相同，逐日重複一次只是佔版面，
 * 改在表格最左側獨立一欄顯示。
 */
export const DATE_SUB_COLUMNS = [
  {
    key: 'left_qnt',
    label: '現場可售',
    primary: true,
    // 負數幾乎都不是真的超賣：全部 39 筆負數中，只有 3 筆 ORDER_QNT+USE_QNT > ROOM_QNT，
    // 其餘 36 筆是鎖控沒釋出造成的帳面效果 (例如 14 間房住滿 14 人、鎖控 2 間仍掛著 → 可售 −2)。
    tip: '櫃台/直訂可以立刻賣的房數 (已扣掉已訂、在住、維修與線上銷售)。負數多半代表鎖控沒有釋出，而非真的賣超過房間數',
  },
  {
    key: 'wrs_left',
    label: '線上銷售',
    // 這是「剩餘」而非「原始配額」：OTA 賣出時德安直接減少 BLOCK_QNT，LEFT_QNT 隨之同降，
    // 所以快照上兩者永遠相等 (BLOCKINV_MN.ORDER_QNT 恆為 0 只代表那個欄位沒被使用，
    // 不代表鎖控沒被消耗)。佐證：鎖控總量隨日期接近而遞減 (未來月份約 1,157 房晚、已過月份約 683)。
    //
    // 通路配額的標準行為：OTA 賣出 → 線上銷售 −1、已訂 +1、現場可售不變；
    //                     現場賣出 → 現場可售 −1、已訂 +1、線上銷售不變。
    tip: '目前仍保留給網路通路 (Booking、易遊網、Expedia、官網等)、尚未被訂走的房數，OTA 賣出後會減少。這些房 OTA 賣得到；現場要賣則需請訂房組釋出',
  },
  {
    key: 'nonwrs_left',
    label: '其他保留',
    tip: 'WRS 以外的鎖控碼 (連通房、過年等)。最後一次使用是 2023/10，之後恆為 0',
  },
  {
    key: 'over_limit',
    label: '超收',
    tip: '允許超訂的額度。近年僅疫情期間設定過，最近一筆非零為 2026/05/01，其餘日期為 0',
  },
  {
    key: 'vacant_total',
    label: '未售小計',
    subtotal: true,
    // 已對 Oracle 驗證 (37,349 筆全數成立)：
    //   LEFT_QNT + LEFT_BLOCK = ROOM_QNT - ORDER_QNT - USE_QNT - OOO_QNT
    // 而 LEFT_BLOCK = 線上銷售 + 其他保留，故此小計即當日尚未賣出的房間數。
    //
    // 刻意不叫「空房」：德安兩個畫面的「空房」是不同口徑 ——
    //   房況畫面的空房 (ROOM_STA='V') = 本欄，實測 2026/08/03 兩邊皆 42
    //   庫存畫面的空房 (LEFT_QNT)     = 本表的「現場可售」，已扣掉鎖控
    // 沿用「空房」會被拿去對照庫存畫面而對不起來。
    compute: (cell) => cell.left_qnt + cell.wrs_left + cell.nonwrs_left,
    tip: '當日還能賣的房間總數 = 現場可售 + 線上銷售 + 其他保留，等於總房數扣掉已訂、在住與維修。其中現場可售可以立刻賣，線上銷售要走 OTA 或請訂房組釋出。對應德安「房況」畫面的空房；德安「庫存」畫面的空房則是本表的「現場可售」。超收不計入 —— 它是超訂額度，不是房間',
  },
];

function indexByDate(days) {
  const byDate = {};
  (days || []).forEach((d) => {
    byDate[d.date] = d;
  });
  return byDate;
}

/**
 * 日期軸 (表格的欄)。
 *
 * API 只會回傳 RMINV_MN 內存在的日期。缺漏的日期必須標成 available: false 讓該欄顯示
 * 「未開放」，絕不可顯示 0 —— 業務會讀成沒房。
 */
const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

export function buildDateColumns(from, to, days = []) {
  const byDate = indexByDate(days);
  const columns = [];
  const cursor = moment(from, DATE_FORMAT);
  const end = moment(to, DATE_FORMAT);

  while (cursor.isSameOrBefore(end, 'day')) {
    const date = cursor.format(DATE_FORMAT);
    const weekday = cursor.day();

    columns.push({
      key: date,
      date,
      // 欄頭只顯示月/日 —— 一次要並排十幾欄，年份每欄重複一次沒有資訊量
      label: cursor.format('MM/DD'),
      weekdayLabel: WEEKDAY_LABELS[weekday],
      isWeekend: weekday === 0 || weekday === 6,
      available: Boolean(byDate[date]),
    });

    cursor.add(1, 'day');
  }

  return columns;
}

/**
 * 房型軸 (表格的列)。每個房型一列，cells 以日期為 key，各項指標由日期底下的子欄位呈現。
 *
 * 查無資料的格子一律給 null (而非 0)，由畫面決定顯示 —— 理由同 buildDateColumns。
 */
export function buildRoomTypeRows(dateColumns = [], days = [], roomTypes = []) {
  const byDate = indexByDate(days);

  return (roomTypes || []).map((type) => {
    const cells = {};
    (dateColumns || []).forEach((col) => {
      const day = byDate[col.date];
      cells[col.date] = day ? (day.types[type.cod] || null) : null;
    });

    return {
      key: type.cod,
      cod: type.cod,
      nam: type.nam,
      room_qnt: type.room_qnt,
      cells,
    };
  });
}

/**
 * 通路調控提醒：房還有得賣，卻有一邊通路完全沒庫存 —— 這種格子要請訂房組調整配置。
 *
 * 兩種失衡：
 *   現場可售 0 → 房都押在線上銷售，櫃台/直訂接不了單，需請訂房組釋出
 *   線上銷售 0 → OTA 已無房可賣，需加配額給網路通路
 *
 * 門檻 2 間 (含)：只剩 1 間時通路失衡的影響有限，標出來反而變成雜訊。
 */
export const REBALANCE_MIN_UNSOLD = 2;

/**
 * 清單預設只看未售 3 間以上。
 *
 * 判定規則的底線是 2 間 (REBALANCE_MIN_UNSOLD)，但只差 2 間的失衡調不調影響有限，
 * 每天卻佔掉清單一大截。門檻做成可調的而不是直接改判定規則 —— 規則一改，
 * 矩陣頁標橘底的格子也會跟著少，那是另一件事，兩頁的判準必須一致。
 */
export const REBALANCE_DEFAULT_MIN_UNSOLD = 3;

/**
 * 兩種調控動作。字串同時出現在清單的篩選器、標籤顏色對照與通知信裡，
 * 集中在這裡定義，改字時不會有地方漏改而讓篩選比對不到。
 */
export const REBALANCE_ACTION_RELEASE = '釋出給現場';
export const REBALANCE_ACTION_ALLOT = '加配額給 OTA';

/**
 * 清單的建議篩選選項與排序次序都取自這一份，順序即畫面上的先後 ——
 * 篩選器、排序、儲存值的合法性檢查各自列一份的話，改動作時會漏掉其中一處。
 */
export const REBALANCE_ACTIONS = [REBALANCE_ACTION_RELEASE, REBALANCE_ACTION_ALLOT];

/**
 * 待調控清單可選的涵蓋天數，與矩陣的查詢區間脫鉤 —— 矩陣是用來看細節的，
 * 清單則要一次看完所有待處理項目，兩者的合適跨度不同。
 * 最大 120 天在後端上限 (124 天) 之內。
 *
 * 預設 30 天：這是每天要走一遍的作業，近月的失衡才調得動 ——
 * 拉到三個月會讓當下處理不了的遠期項目佔掉大半版面。要看更遠的自己往上調。
 */
export const REBALANCE_DAY_OPTIONS = [30, 60, 90, 120];
export const REBALANCE_DAYS = 30;

export function rebalanceHint(cell) {
  if (!cell) return null;

  const unsold = cell.left_qnt + cell.wrs_left + cell.nonwrs_left;
  if (unsold < REBALANCE_MIN_UNSOLD) return null;

  if (cell.left_qnt <= 0) {
    return {
      key: 'left_qnt',
      action: REBALANCE_ACTION_RELEASE,
      message: `尚有 ${unsold} 間未售，但現場可售為 ${cell.left_qnt} —— 房都押在線上銷售，櫃台與直訂接不了單，建議請訂房組釋出一部分`,
    };
  }

  if (cell.wrs_left <= 0) {
    return {
      key: 'wrs_left',
      action: REBALANCE_ACTION_ALLOT,
      // 「線上通路」含飯店官網 —— WRS 是共用的一份，官網也從這裡扣
      message: `尚有 ${unsold} 間未售，但線上銷售為 0 —— 所有線上通路 (含官網) 都已無房可賣，建議加配額`,
    };
  }

  return null;
}

/**
 * 把矩陣裡所有待調控的格子攤平成一份清單，供訂房組逐項處理。
 * 矩陣適合看全局，但要逐一處理時橫向十幾組日期會很難掃 —— 兩者互補。
 */
export function buildRebalanceList(dateColumns = [], rows = []) {
  const list = [];

  (rows || []).forEach((row) => {
    (dateColumns || []).forEach((col) => {
      const cell = row.cells ? row.cells[col.date] : null;
      const hint = rebalanceHint(cell);
      if (!hint) return;

      list.push({
        key: `${col.date}:${row.cod}`,
        date: col.date,
        cod: row.cod,
        nam: row.nam,
        room_qnt: row.room_qnt,
        left_qnt: cell.left_qnt,
        wrs_left: cell.wrs_left,
        unsold: cell.left_qnt + cell.wrs_left + cell.nonwrs_left,
        action: hint.action,
        message: hint.message,
      });
    });
  });

  // 依日期排序，訂房組是照日曆逐日處理的；同一天內依未售量由多到少，先看影響大的
  list.sort((a, b) => (a.date === b.date ? b.unsold - a.unsold : a.date.localeCompare(b.date)));

  return list;
}

/**
 * 連續入住的可售上限 = 區間內每日「未售總量」的最小值 (瓶頸日)。
 *
 * 以未售總量 (現場可售 + 線上銷售 + 其他保留) 而非只看現場可售 —— 這一段要回答的是
 * 「整段都要住的話最多能接幾間」，兩邊通路的房都算數，線上那部分請訂房組釋出即可轉現場。
 * 只看現場可售會低估，把調得動的房排除在外。
 *
 * 客人要從頭住到尾，能賣的就是最少的那一天，所以是取 min 而非加總。
 * 未開放的日期沒有資料，不可當成 0 —— 那會把結果壓成 0、誤判成不能賣；
 * 改為排除並回報缺了幾天，讓畫面可以標示這個數字只採計了部分日期。
 */
export function buildContinuousAvailability(dateColumns = [], cells = {}) {
  let value = null;
  let counted = 0;
  let missing = 0;

  (dateColumns || []).forEach((col) => {
    const cell = cells[col.date];
    if (!cell) {
      missing += 1;
      return;
    }
    counted += 1;
    const sellable = cell.left_qnt + cell.wrs_left + cell.nonwrs_left;
    if (value === null || sellable < value) {
      value = sellable;
    }
  });

  return { value, counted, missing };
}

/**
 * 底部「可售合計」列的格子，同樣以日期為 key。
 */
export function buildTotalCells(dateColumns = [], days = []) {
  const byDate = indexByDate(days);
  const cells = {};

  (dateColumns || []).forEach((col) => {
    const day = byDate[col.date];
    cells[col.date] = day ? day.total : null;
  });

  return cells;
}
